import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { useMemo } from "react";

import { useGetQualifications } from "../Qualifications/api/useGetQualifications";
import { useIsSurfaceShiftsEnabled } from "../redesign/ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { useDefinedWorker } from "./useDefinedWorker";

export const WORKER_QUALIFICATIONS_QUERY_KEY = "use-worker-qualifications";

/**
 * This function is used to get the qualifications for the current worker.
 * Based on the feature flag it will get them from the shoppable qualifications or the licenses data.
 *
 * It then calls the useGetQualifications hook to get the qualifications data and returns a complete
 * qualification objects. These come sorted by name
 */
export function useWorkerQualifications(options?: {
  // Allow caller to avoid redundantly refetching on window focus
  refetchOnWindowFocus?: boolean;
}) {
  const { licensesData, shoppableQualifications } = useDefinedWorker();

  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();

  const {
    data: qualificationsData,
    isLoading: qualificationsAreLoading,
    isFetching: qualificationsAreFetching,
    isError: qualificationsAreError,
    error: qualificationsError,
    isSuccess: qualificationsAreLoaded,
    refetch: refetchQualifications,
    status,
  } = useGetQualifications(
    // this is an api filter, not react-query enabled
    { enabled: true },
    {
      ...(options?.refetchOnWindowFocus && { refetchOnWindowFocus: options.refetchOnWindowFocus }),
    }
  );

  const workerQualifications = useMemo(() => {
    if (qualificationsAreError || !qualificationsData) {
      return [];
    }

    let qualifications: Qualification[] = [];

    if (isSurfaceShiftsEnabled) {
      const qualificationIds = new Set(
        shoppableQualifications.map(
          (shoppableQualification) => shoppableQualification.relationships.qualification.data.id
        )
      );

      qualifications =
        qualificationsData.data.filter((qualification) => qualificationIds.has(qualification.id)) ??
        [];
    } else {
      const qualificationNames = new Set(licensesData.map((license) => license.qualification));

      qualifications =
        qualificationsData.data.filter((qualification) =>
          qualificationNames.has(qualification.attributes.name)
        ) ?? [];
    }

    qualifications.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));
    return qualifications;
  }, [
    shoppableQualifications,
    licensesData,
    isSurfaceShiftsEnabled,
    qualificationsData,
    qualificationsAreError,
  ]);

  return {
    data: workerQualifications,
    isLoading: qualificationsAreLoading,
    isFetching: qualificationsAreFetching,
    isError: qualificationsAreError,
    error: qualificationsError,
    isSuccess: qualificationsAreLoaded,
    refetch: refetchQualifications,
    status,
  };
}
